// ============================================================================
// BESPOKIAN Health - Contact Page
// Contact form with luxury styling
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';

export function ContactPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Get In Touch
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              We're Here To Help
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Have questions about pricing, prescriptions, or our services? 
              Our team is ready to assist you.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <div className="w-12 h-0.5 bg-gold-500 mb-6" />
                <h2 className="font-display text-2xl text-navy-800 tracking-tight italic mb-8">Contact Information</h2>

                <div className="space-y-4">
                  <a
                    href="tel:1-877-237-7679"
                    className="flex items-start gap-4 p-5 bg-sage-50 rounded-xl border border-sage-200 hover:border-sage-300 transition-colors"
                  >
                    <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-sage-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-800">Pharmacist Line</h3>
                      <p className="text-gold-600 font-medium">1-877-BES-PORX</p>
                      <p className="text-sm text-navy-500 mt-1">Support available</p>
                    </div>
                  </a>

                  <a
                    href="mailto:hello@bespokianhealth.com"
                    className="flex items-start gap-4 p-5 bg-gold-50 rounded-xl border border-gold-200 hover:border-gold-300 transition-colors"
                  >
                    <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-gold-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-800">Email</h3>
                      <p className="text-gold-600 font-medium">hello@bespokianhealth.com</p>
                      <p className="text-sm text-navy-500 mt-1">Response within 24 hours</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-5 bg-navy-50 rounded-xl border border-navy-100">
                    <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-navy-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-800">Headquarters</h3>
                      <p className="text-navy-600">123 Pharmacy Way</p>
                      <p className="text-navy-600">Altamonte Springs, FL</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-navy-50 rounded-xl border border-navy-100">
                    <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-navy-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-800">Support Hours</h3>
                      <p className="text-navy-600">Pharmacist support available</p>
                      <p className="text-navy-600">Customer Service: Mon-Fri 8am-8pm ET</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-navy-50 rounded-2xl p-8 border border-navy-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-gold-600" />
                  </div>
                  <h2 className="font-display text-xl text-navy-800 tracking-tight">Send a Message</h2>
                </div>

                <form className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-navy-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-3 rounded-lg border border-navy-200 bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-navy-800 placeholder-navy-400"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-3 rounded-lg border border-navy-200 bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-navy-800 placeholder-navy-400"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-navy-700 mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      className="w-full px-4 py-3 rounded-lg border border-navy-200 bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-navy-800"
                    >
                      <option value="">Select a topic...</option>
                      <option value="pricing">Pricing Question</option>
                      <option value="prescription">Prescription Inquiry</option>
                      <option value="transfer">Transfer Prescription</option>
                      <option value="telemedicine">Telemedicine</option>
                      <option value="employer">Employer Benefits</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-navy-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-navy-200 bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-navy-800 placeholder-navy-400 resize-none"
                      placeholder="How can we help you?"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3.5 text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg font-semibold transition-all hover:shadow-glow-gold"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



